import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type {
  SupplierDetail,
  SupplierCreatePayload,
  SupplierTransport,
} from "../../api/types";
import { S } from "../../styles";
import { LoadingState, ErrorState } from "../../components/QueryStates";
import { th, td, tableStyle } from "./adminStyles";

// Dispatch channels the RFQ workflow can use to reach a supplier.
// Mirrors app/adapters/transport.py::TRANSPORTS — the API rejects anything else.
const TRANSPORTS: {
  value: SupplierTransport;
  label: string;
  hint: string;
}[] = [
  {
    value: "rest",
    label: "REST / Aeroxchange",
    hint: "RFQs are POSTed to the supplier's API; quotes come back to the callback URL.",
  },
  {
    value: "email",
    label: "Email (SMTP + IMAP)",
    hint: "RFQs are emailed to the contact address; PDF quote replies are read back over IMAP.",
  },
  {
    value: "edi_x12",
    label: "EDI — X12",
    hint: "RFQs go out as an X12 840 to the EDI endpoint; 843 quotes come back to /inbound/edi.",
  },
  {
    value: "edi_edifact",
    label: "EDI — EDIFACT",
    hint: "RFQs go out as a REQOTE to the EDI endpoint; QUOTES come back to /inbound/edi.",
  },
];

const TRANSPORT_LABELS: Record<SupplierTransport, string> = {
  rest: "REST",
  email: "Email",
  edi_x12: "EDI X12",
  edi_edifact: "EDI EDIFACT",
};

interface FormState {
  name: string;
  code: string;
  contact_email: string;
  contact_phone: string;
  country: string;
  certifications: string;
  is_active: boolean;
  transport: SupplierTransport;
  edi_endpoint: string;
}

const EMPTY: FormState = {
  name: "",
  code: "",
  contact_email: "",
  contact_phone: "",
  country: "",
  certifications: "",
  is_active: true,
  transport: "rest",
  edi_endpoint: "",
};

function toCerts(raw: string): string[] | null {
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : null;
}

export function SuppliersPanel() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<FormState>({ ...EMPTY });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>({ ...EMPTY });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-suppliers"],
    queryFn: () => api.listSuppliersDetail(),
  });
  const suppliers = data?.items ?? [];

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ["admin-suppliers"] });

  const createMutation = useMutation({
    mutationFn: (payload: SupplierCreatePayload) => api.createSupplier(payload),
    onSuccess: (s) => {
      setMsg(`✅ Created supplier ${s.name}`);
      setCreateForm({ ...EMPTY });
      setShowCreate(false);
      invalidate();
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, form }: { id: string; form: FormState }) =>
      api.updateSupplier(id, {
        name: form.name.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim() || null,
        country: form.country.trim() || null,
        certifications: toCerts(form.certifications),
        is_active: form.is_active,
        transport: form.transport,
        edi_endpoint: form.edi_endpoint.trim() || null,
      }),
    onSuccess: (s) => {
      setMsg(`✅ Updated ${s.name}`);
      setEditingId(null);
      invalidate();
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteSupplier(id),
    onSuccess: () => {
      setMsg("✅ Supplier removed");
      invalidate();
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const startEdit = (s: SupplierDetail) => {
    setEditingId(s.id);
    setEditForm({
      name: s.name,
      code: s.code,
      contact_email: s.contact_email,
      contact_phone: s.contact_phone ?? "",
      country: s.country ?? "",
      certifications: (s.certifications ?? []).join(", "),
      is_active: s.is_active,
      transport: s.transport,
      edi_endpoint: s.edi_endpoint ?? "",
    });
  };

  const submitCreate = () =>
    createMutation.mutate({
      name: createForm.name.trim(),
      code: createForm.code.trim(),
      contact_email: createForm.contact_email.trim(),
      contact_phone: createForm.contact_phone.trim() || null,
      country: createForm.country.trim() || null,
      certifications: toCerts(createForm.certifications),
      is_active: createForm.is_active,
      transport: createForm.transport,
      edi_endpoint: createForm.edi_endpoint.trim() || null,
    });

  const canCreate =
    createForm.name.trim() &&
    createForm.code.trim() &&
    createForm.contact_email.trim();

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>🏭 Suppliers</h2>
        <button style={S.btnSm} onClick={() => void refetch()}>
          ↺ Refresh
        </button>
        <button style={S.btn} onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? "Cancel" : "+ New Supplier"}
        </button>
      </div>
      <p style={{ ...S.muted, marginTop: 0, marginBottom: 16 }}>
        Manage the supplier master for this tenant.
      </p>

      {msg && (
        <div style={{ ...S.card, background: "#eef2ff", marginBottom: 12 }}>
          {msg}
        </div>
      )}

      {showCreate && (
        <div style={S.card}>
          <h3 style={S.h3}>Create supplier</h3>
          <SupplierFields
            form={createForm}
            onChange={setCreateForm}
            codeEditable
          />
          <div style={{ marginTop: 12 }}>
            <button
              style={{ ...S.btn, opacity: canCreate ? 1 : 0.5 }}
              disabled={!canCreate || createMutation.isPending}
              onClick={submitCreate}
            >
              {createMutation.isPending ? "Creating…" : "Create supplier"}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <LoadingState label="Loading suppliers…" />
      ) : error ? (
        <ErrorState error={error} />
      ) : suppliers.length === 0 ? (
        <div style={S.card}>
          <p style={S.muted}>No suppliers yet.</p>
        </div>
      ) : (
        <div style={{ ...S.card, overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Supplier</th>
                <th style={th}>Contact</th>
                <th style={th}>Channel</th>
                <th style={th}>Country</th>
                <th style={th}>Certifications</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <tr key={s.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                    <div style={S.muted}>{s.code}</div>
                  </td>
                  <td style={td}>
                    <div>{s.contact_email}</div>
                    {s.contact_phone && (
                      <div style={S.muted}>{s.contact_phone}</div>
                    )}
                  </td>
                  <td style={td}>
                    {TRANSPORT_LABELS[s.transport] ?? s.transport}
                  </td>
                  <td style={td}>{s.country ?? "—"}</td>
                  <td style={td}>
                    {(s.certifications ?? []).join(", ") || "—"}
                  </td>
                  <td style={td}>
                    <span
                      style={S.statusBadge(s.is_active ? "Quoted" : "Expired")}
                    >
                      {s.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td
                    style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}
                  >
                    <button style={S.btnGhost} onClick={() => startEdit(s)}>
                      Edit
                    </button>{" "}
                    <button
                      style={{ ...S.btnGhost, color: "#b91c1c" }}
                      onClick={() => {
                        if (window.confirm(`Remove supplier ${s.name}?`))
                          deleteMutation.mutate(s.id);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingId && (
        <div style={{ ...S.card, borderColor: "#c7d2fe" }}>
          <h3 style={S.h3}>
            Edit supplier — <span style={S.muted}>{editForm.code}</span>
          </h3>
          <SupplierFields form={editForm} onChange={setEditForm} />
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button
              style={S.btn}
              disabled={updateMutation.isPending}
              onClick={() =>
                updateMutation.mutate({ id: editingId, form: editForm })
              }
            >
              {updateMutation.isPending ? "Saving…" : "Save changes"}
            </button>
            <button style={S.btnGhost} onClick={() => setEditingId(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SupplierFields({
  form,
  onChange,
  codeEditable = false,
}: {
  form: FormState;
  onChange: (f: FormState) => void;
  codeEditable?: boolean;
}) {
  return (
    <>
      <div style={S.grid2}>
        <div>
          <label style={S.label}>Name</label>
          <input
            style={S.input}
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <label style={S.label}>Code</label>
          <input
            style={{ ...S.input, opacity: codeEditable ? 1 : 0.6 }}
            value={form.code}
            disabled={!codeEditable}
            onChange={(e) => onChange({ ...form, code: e.target.value })}
          />
        </div>
        <div>
          <label style={S.label}>Contact email</label>
          <input
            style={S.input}
            value={form.contact_email}
            onChange={(e) =>
              onChange({ ...form, contact_email: e.target.value })
            }
          />
        </div>
        {form.transport.startsWith("edi_") && (
          <div>
            <label style={S.label}>EDI endpoint</label>
            <input
              style={S.input}
              value={form.edi_endpoint}
              onChange={(e) =>
                onChange({ ...form, edi_endpoint: e.target.value })
              }
              placeholder="https://partner.example.com/edi/inbound"
            />
            <div style={{ ...S.muted, marginTop: -6 }}>
              Where EDI messages are delivered. Dispatch fails without it.
            </div>
          </div>
        )}
        <div>
          <label style={S.label}>Contact phone</label>
          <input
            style={S.input}
            value={form.contact_phone}
            onChange={(e) =>
              onChange({ ...form, contact_phone: e.target.value })
            }
          />
        </div>
        <div>
          <label style={S.label}>Country</label>
          <input
            style={S.input}
            value={form.country}
            onChange={(e) => onChange({ ...form, country: e.target.value })}
            placeholder="US"
          />
        </div>
        <div>
          <label style={S.label}>Certifications (comma-separated)</label>
          <input
            style={S.input}
            value={form.certifications}
            onChange={(e) =>
              onChange({ ...form, certifications: e.target.value })
            }
            placeholder="FAA, EASA"
          />
        </div>
        <div>
          <label style={S.label}>Dispatch channel</label>
          <select
            style={S.select}
            value={form.transport}
            onChange={(e) =>
              onChange({
                ...form,
                transport: e.target.value as SupplierTransport,
              })
            }
          >
            {TRANSPORTS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <div style={{ ...S.muted, marginTop: -6 }}>
            {TRANSPORTS.find((t) => t.value === form.transport)?.hint}
          </div>
        </div>
      </div>
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={(e) => onChange({ ...form, is_active: e.target.checked })}
        />
        Active
      </label>
    </>
  );
}
